import React, { useCallback } from 'react';

import {
  usePermissions,
  useRecordContext,
  EditBase,
  useRefresh,
  useNotify,
  Link,
  useCreatePath,
  useResourceContext,
  TabbedShowLayout,
  SimpleForm,
} from 'react-admin';

import {
  Box,
  Typography,
  Dialog,
  IconButton,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import hasAccess from '../../../utilities/hasAccess';

import useDialogStatus from '../../../hooks/useDialogStatus';
import actions, { Actions } from '../../../constants/actions';
import { ResourceTypes } from '../../../constants/resourceTypes';
import errorNotification from '../../../utilities/errorNotification';
import AuditResourceList from '../../audit/AuditResourceList';

const EditDialog = ({
  customToolbar,
  title,
  children,
  action,
  resourceType,
}: {
  customToolbar: React.ReactElement;
  title: string;
  children: React.ReactElement;
  action: Actions;
  resourceType: ResourceTypes;
}) => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext<{ id: string }>();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const refresh = useRefresh();
  const notify = useNotify();

  const onSuccess = useCallback(() => {
    closeDialog();
    refresh();
  }, [closeDialog, refresh]);

  const onError = useCallback((e: unknown) => {
    notify(errorNotification(e), { type: 'error' });
  }, [notify]);

  const createPath = useCreatePath();

  const resource = useResourceContext();

  const editPath = createPath({
    resource,
    id: record?.id,
    type: 'edit',
  });

  return (
    <Box>
      <Link
        onClick={(e: React.MouseEvent) => { e.preventDefault(); openDialog(); }}
        to={editPath}
      >
        {record?.id}
      </Link>
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <Box display="flex" alignItems="center" justifyContent="space-between" padding={4} paddingBottom={0}>
          <Typography variant="largeTitle">{`Edit ${title}`}</Typography>
          <IconButton onClick={closeDialog} size="small">
            <CloseIcon color="primary" />
          </IconButton>
        </Box>
        <EditBase
          id={record?.id}
          mutationMode="pessimistic"
          mutationOptions={{ onSuccess, onError }}
          redirect={false}
        >
          <TabbedShowLayout>
            <TabbedShowLayout.Tab label="Information">
              <SimpleForm
                mode="all"
                toolbar={hasAccess(permissions, action) ? customToolbar : false}
                component={Box}
              >
                {children}
              </SimpleForm>
            </TabbedShowLayout.Tab>
            {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
              <TabbedShowLayout.Tab label="Audit trail">
                <AuditResourceList resourceType={resourceType} />
              </TabbedShowLayout.Tab>
            )}
          </TabbedShowLayout>
        </EditBase>
      </Dialog>
    </Box>
  );
};

export default EditDialog;
